// Deterministic JSON serialization for signature verification. This mirrors the
// reference canonicalizer in `frontend/endpoints/src/canonical.ts` byte-for-byte
// so the client verifies exactly the bytes the offline signer produced.
//
// Rules (a JCS/RFC-8785 subset sufficient for the endpoint-list document):
//   - object keys sorted ascending by UTF-16 code unit (JS default sort);
//   - `undefined`-valued keys are dropped;
//   - no insignificant whitespace; arrays keep their order;
//   - numbers via JSON.stringify (the only number here is an integer version).

export function canonicalize(value: unknown): string {
  return serialize(value);
}

function serialize(v: unknown): string {
  if (v === null) return 'null';
  const t = typeof v;
  if (t === 'number') {
    if (!Number.isFinite(v)) throw new Error('cannot canonicalize non-finite number');
    return JSON.stringify(v);
  }
  if (t === 'boolean' || t === 'string') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(serialize).join(',') + ']';
  if (t === 'object') {
    const obj = v as Record<string, unknown>;
    const keys = Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort();
    return '{' + keys.map((k) => JSON.stringify(k) + ':' + serialize(obj[k])).join(',') + '}';
  }
  throw new Error(`cannot canonicalize value of type ${t}`);
}
